import React, { useState, useEffect, useContext, useRef } from 'react';
import { array, func, bool, string } from 'prop-types';
import classNames from 'classnames/bind';
import { splitEvery } from 'ramda';

import FieldNoResultsTooltip from 'components/FieldNoResultsTooltip';
import Hint from 'components/Hint';
import CommonLabel from 'components/CommonLabel';
import PopinVinInfo from 'components/PopinVinInfo';

import { withErrorBoundary } from 'containers/ErrorBoundary';
import PopinInfoContainer from 'containers/PopinInfoContainer';

import { StaticTextContext } from 'context/StaticTextContext';
import { SearchContext } from 'context/SearchContext';
import { CountryContext } from 'context/CountryContext';

import { getCarsByVin } from 'utils/getCarsByVin';
import { isBlock } from 'utils/browserHistory';
import { getCarDetails } from 'utils/getCarAutosuggestions';

import CheckIcon from 'media/icons/checkmark.icon.svg';
import CrossIcon from 'media/icons/cross.icon.svg';
import styles from './FieldSearchVin.module.scss';
const cx = classNames.bind(styles);

const FieldSearchVin = ({
  pageType,
  isSelectedCar,
  partData,
  setPartValue,
  setPartData,
  setVehiclesData,
  setVinVehicleValue,
}) => {
  const [isNoResults, setIsNoResults] = useState(false);
  const [isError, setIsError] = useState(null);

  const { currentCountry } = useContext(CountryContext);
  const { labels } = useContext(StaticTextContext);
  const {
    setVinNumber,
    setIsErrorPart,
    setIsErrorVinVehicle,
    setIsSelectedVinVehicle,
    setIsVinVehicleTooltip,
    setIsSelectedCar,
    setVinVehicle,
    isSelectedVinVehicle,
    setManuName,
    setManuId,
    setModelName,
    setModelId,
    setCarName,
    setCarId,
    setPartName,
    setPartId,
    vinNumber,
    vinVehicle,
    carName,
    partName,
    vehicleType,
  } = useContext(SearchContext);
  let vinRef = useRef();

  const [currentValue, setCurrentValue] = useState(vinNumber);

  const setClearSearchState = () => {
    if (carName) {
      // Update from props.
      setIsErrorPart(false);
      setPartValue('');
      setIsSelectedCar(false);
      setVinVehicleValue('');

      // Update context.
      // setVinNumber('');
      setCarName('');
      setCarId(null);
      setModelName('');
      setModelId(null);
      setManuName('');
      setManuId(null);
      setPartName('');
      setPartId(null);
    }
  };

  const handleOnBlur = (event) => {
    const currentValue = event.target.value;

    setIsError(currentValue.length < 17 && currentValue.length > 0);
  };

  const handleOnFocus = () => {
    setIsError(false);
  };

  const partRequestSuccess = ({ status, data: response }) => {
    const partField = document.getElementById('search-part');
    if (status === 200 && response.status === 200) {
      const data = response.data.array ? response.data.array : [];
      setPartData(data);
      setIsSelectedCar(true);
      setIsErrorPart(false);

      if (!partName && partField && window.innerWidth > 768) {
        partField.focus();
      }
    }
  };

  const getCarDetailsSuccess = ({ status, data: response }, currentVin) => {
    const vinVehicleField = document.getElementById('vin-model');

    if (status === 200 && response.status === 200) {
      setIsNoResults(false);
      setIsSelectedCar(true);
      setVinNumber(currentVin);
      setVehiclesData(response.data.array);

      if (vinVehicleField && !vinVehicle.length) {
        vinVehicleField.focus();
      }
    }
  };

  const getCarsByVinSuccess = ({ data: response }, currentValue) => {
    const vehiclesCount = response.data.matchingVehiclesCount;

    // JNKCA31A31T039490 - one result match VIN
    // 1GNSCJE06ER120667 - moere then one result match VIN
    if (vehiclesCount === 1) {
      const {
        carName,
        carId,
        manuId,
        modelId,
      } = response.data.matchingVehicles.array[0];

      setCarName(carName);
      setVinVehicle(carName);
      setCarId(carId);
      setModelName('');
      setModelId(modelId);
      setManuName('');
      setManuId(manuId);

      setIsSelectedCar(true);
      setIsSelectedVinVehicle(true);
      setIsErrorVinVehicle(false);
      setVinNumber(currentValue);
      setIsNoResults(false);

    } else if (vehiclesCount > 1) {
      const vehiclesArray = response.data.matchingVehicles.array;
      const idsArray = vehiclesArray.map((item) => item.carId);

      splitEvery(25, idsArray).forEach((group) => {
        getCarDetails(group, currentCountry, vehicleType)
          .then((responseDetails) => {
            getCarDetailsSuccess(responseDetails, currentValue);
          })
          .catch(() => {
            setIsNoResults(true);
            setVehiclesData([]);
          });
      });
    } else {
      setIsNoResults(true);
      setVehiclesData([]);
    }
  };

  const handleOnChange = (event) => {
    const currentValue = event.target.value;
    setCurrentValue(currentValue);
    setPartData([]);
    setPartValue('');
    setPartName('');
    setPartId(null);
    setIsErrorPart(false);
    setVehiclesData([]);
    setVinVehicle('');
    setVinVehicleValue('');
    setIsErrorVinVehicle(false);
    setIsSelectedVinVehicle(false);
    setIsVinVehicleTooltip(false);

    if (currentValue.length === 17) {
      getCarsByVin(currentValue, currentCountry)
        .then((response) => {
          getCarsByVinSuccess(response, currentValue);
        })
        .catch(() => {
          setIsNoResults(false);
          setClearSearchState();
        });
    } else {
      setClearSearchState();
      setIsNoResults(false);
    }
  };

  useEffect(() => {
    if (currentCountry !== '.com') {
      !isBlock && vinRef.current && vinRef.current.focus();
    }

    if (vinNumber && !partData.length) {
      getCarsByVin(vinNumber, currentCountry)
        .then((response) => {
          getCarsByVinSuccess(response, vinNumber);
        })
        .catch(() => {
          setIsNoResults(false);
          setClearSearchState();
        });
    }
  }, [currentCountry]);

  // Lenght of input value is equal to 17, because it is the standart of VIN number length.
  return (
    <div className={cx('search-car')}>
      <div className={cx('buttons-group')}>
        <CommonLabel className={cx('label')} htmlFor="search-vin">
          {labels.searchVinLabel}
        </CommonLabel>

        <PopinInfoContainer>
          <PopinVinInfo />
        </PopinInfoContainer>
      </div>

      <div
        className={cx('form-control-wrap', 'vin-search', {
          'is-filled': isSelectedCar,
        })}
      >
        <input
          className={cx('form-control', {
            'is-error': isError,
            'is-filled': isSelectedCar,
          })}
          onBlur={handleOnBlur}
          onFocus={handleOnFocus}
          onChange={handleOnChange}
          value={currentValue}
          type="text"
          id="search-vin"
          minLength="17"
          maxLength="17"
          ref={vinRef}
          disabled={currentCountry === '.com'}
          placeholder={labels.searchVinPlaceholder}
          required
          autoFocus={!isBlock && window.innerWidth > 768}
        />
        {isError && (
          <div className={cx('error-message', 'is-error')}>
            {labels.errorVinNumber}
          </div>
        )}
        {isNoResults && (
          <FieldNoResultsTooltip
            title={labels.vinTooltipTitle}
            description={labels.vinTooltipDescription}
            className={cx('tooltip')}
          />
        )}
        {pageType === 'home' && (
          <Hint
            className={cx('hint', {
              'is-filled': isSelectedVinVehicle,
            })}
          >
            {!isSelectedCar ? (
              <span className={cx('hint-text')}>{labels.tipVinInput}</span>
            ) : (
              <span className={cx('hint-text')}>{labels.tipPartInput}</span>
            )}
          </Hint>
        )}
        <div className={cx('icon-check')}>
          <CheckIcon />
        </div>
        {isSelectedCar && (
          <button
            className={cx('cross-btn')}
            type="button"
            onClick={() => handleOnChange({ target: { value: '' } })}
          >
            <CrossIcon />
          </button>
        )}
      </div>
    </div>
  );
};

FieldSearchVin.propTypes = {
  pageType: string.isRequired,
  isSelectedCar: bool.isRequired,
  partData: array,
  setPartData: func.isRequired,
  setPartValue: func.isRequired,
  setVehiclesData: func.isRequired,
  setVinVehicleValue: func.isRequired,
};

FieldSearchVin.defaultProps = {
  partData: [],
};

export default withErrorBoundary(FieldSearchVin);



// WEBPACK FOOTER //
// ./src/components/SearchByVin/components/FieldSearchVin/FieldSearchVin.js